import { useState, useEffect, useMemo } from 'react';
import activityService, {
  type ActivityPeriod,
  type ActivityAvailability,
  computeActivityRanges,
} from '../../api/activityService';
import forecastService, {
  type ForecastDaily,
  type ForecastHourly,
} from '../../api/forecastService';
import type { ForecastAvailability } from '../../components/Timeline/types';
import type { EventWithCircuit } from '../../api/eventsService';
import { getEventSource } from '../../utils/eventSource';

interface UseTimelineAvailabilityReturn {
  radarActivityAvailability: ActivityAvailability | undefined;
  stationActivityAvailability: ActivityAvailability | undefined;
  forecastAvailability: ForecastAvailability;
  loading: boolean;
}

export function useTimelineAvailability(
  selectedEvent: EventWithCircuit | null | undefined,
  refreshCounter: number = 0,
): UseTimelineAvailabilityReturn {
  const [radarActivity, setRadarActivity] = useState<ActivityPeriod[]>([]);
  const [stationActivity, setStationActivity] = useState<ActivityPeriod[]>([]);
  const [dailyForecasts, setDailyForecasts] = useState<ForecastDaily[]>([]);
  const [hourlyForecasts, setHourlyForecasts] = useState<ForecastHourly[]>([]);
  const [loading, setLoading] = useState(false);

  const observationsEnabled = selectedEvent?.observations_enabled !== false;

  useEffect(() => {
    if (!selectedEvent) {
      setRadarActivity([]);
      setStationActivity([]);
      setDailyForecasts([]);
      setHourlyForecasts([]);
      return;
    }

    const source = getEventSource(selectedEvent);
    const radarSource = source === 'legacy' ? 'legacy' : 'l2';

    setLoading(true);
    const fetchAll = async () => {
      const activityPromises: Promise<Awaited<ReturnType<typeof activityService.getRadarActivity>>>[] =
        observationsEnabled
          ? [
              activityService.getRadarActivity({
                season_year: selectedEvent.season_year,
                circuit_id: selectedEvent.circuit_id,
                start_date: selectedEvent.start_date,
                source: radarSource,
              }),
              activityService.getStationActivity({
                season_year: selectedEvent.season_year,
                circuit_id: selectedEvent.circuit_id,
                start_date: selectedEvent.start_date,
                source,
              }),
            ]
          : [
              Promise.resolve({ success: true, count: 0, activities: [] as ActivityPeriod[] }),
              Promise.resolve({ success: true, count: 0, activities: [] as ActivityPeriod[] }),
            ];

      const [radarRes, stationRes, dailyRes, hourlyRes] = await Promise.allSettled([
        ...activityPromises,
        forecastService.getForecastDaily({
          season_year: selectedEvent.season_year,
          circuit_id: selectedEvent.circuit_id,
          start_date: selectedEvent.start_date,
          source,
        }),
        forecastService.getForecastHourly({
          season_year: selectedEvent.season_year,
          circuit_id: selectedEvent.circuit_id,
          start_date: selectedEvent.start_date,
          source,
        }),
      ]);

      if (observationsEnabled) {
        if (radarRes.status === 'fulfilled') {
          const val = radarRes.value as { activities: ActivityPeriod[] };
          setRadarActivity(val.activities?.length ? val.activities : []);
        } else {
          setRadarActivity([]);
        }

        if (stationRes.status === 'fulfilled') {
          const val = stationRes.value as { activities: ActivityPeriod[] };
          setStationActivity(val.activities?.length ? val.activities : []);
        } else {
          setStationActivity([]);
        }
      } else {
        setRadarActivity([]);
        setStationActivity([]);
      }

      if (dailyRes.status === 'fulfilled') {
        const val = dailyRes.value as { forecasts?: ForecastDaily[] };
        setDailyForecasts(val.forecasts?.length ? val.forecasts : []);
      } else {
        setDailyForecasts([]);
      }

      if (hourlyRes.status === 'fulfilled') {
        const val = hourlyRes.value as { forecasts?: ForecastHourly[] };
        setHourlyForecasts(val.forecasts?.length ? val.forecasts : []);
      } else {
        setHourlyForecasts([]);
      }
    };

    fetchAll()
      .catch((err) => {
        console.error('[Dashboard] Error fetching timeline availability:', err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedEvent, observationsEnabled, refreshCounter]);

  const radarActivityAvailability = useMemo(() => {
    if (!observationsEnabled || radarActivity.length === 0) return undefined;
    return computeActivityRanges(radarActivity);
  }, [observationsEnabled, radarActivity]);

  const stationActivityAvailability = useMemo(() => {
    if (!observationsEnabled || stationActivity.length === 0) return undefined;
    return computeActivityRanges(stationActivity);
  }, [observationsEnabled, stationActivity]);

  const forecastAvailability = useMemo<ForecastAvailability>(() => {
    const availability: ForecastAvailability = {};

    if (dailyForecasts.length > 0) {
      availability.daily = dailyForecasts.map((f) => new Date(f.value_date));
    }

    if (hourlyForecasts.length > 0) {
      availability.hourlyIssuance = hourlyForecasts.map((f) => new Date(f.value_date));

      const ranges: { start: Date; end: Date }[] = [];
      hourlyForecasts.forEach((forecast) => {
        if (forecast.hourly_forecasts?.length) {
          const times: number[] = [];
          forecast.hourly_forecasts.forEach((hf) => {
            const dtVal =
              typeof hf.forecast_datetime === 'object' &&
              hf.forecast_datetime !== null &&
              'value' in hf.forecast_datetime
                ? (hf.forecast_datetime as { value: string }).value
                : hf.forecast_datetime;
            if (dtVal) times.push(new Date(dtVal as string).getTime());
          });
          if (times.length > 0) {
            ranges.push({
              start: new Date(Math.min(...times)),
              end: new Date(Math.max(...times)),
            });
          }
        }
      });
      if (ranges.length > 0) {
        availability.hourly = ranges;
      }
    }

    return availability;
  }, [dailyForecasts, hourlyForecasts]);

  return {
    radarActivityAvailability,
    stationActivityAvailability,
    forecastAvailability,
    loading,
  };
}
